import React, { useEffect, useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Link, useNavigate } from 'react-router-dom';
import Topbar from '../components/Topbar';
import axios from '../libs/axios';

export default function CreateJob({ token }) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [excelFile, setExcelFile] = useState(null);
  const [pdfFiles, setPdfFiles] = useState([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [loadingClients, setLoadingClients] = useState(false);
  const [savedClient, setSavedClient] = useState(null);
  const [showExcelFormatModal, setShowExcelFormatModal] = useState(false);
  
  // EXTF Header fields
  const [extfAdvisorNumber, setExtfAdvisorNumber] = useState('');
  const [extfClientNumber, setExtfClientNumber] = useState('');
  const [extfFiscalYearStart, setExtfFiscalYearStart] = useState('');
  const [extfAccountLength, setExtfAccountLength] = useState('4');
  const [extfPeriodStart, setExtfPeriodStart] = useState('');
  const [extfPeriodEnd, setExtfPeriodEnd] = useState('');
  const [extfBatchDescription, setExtfBatchDescription] = useState('');
  const [extfDictationAbbr, setExtfDictationAbbr] = useState('');
  const [extfBookingType, setExtfBookingType] = useState('1');
  const [extfAccountingPurpose, setExtfAccountingPurpose] = useState('0');
  const [extfFinalization, setExtfFinalization] = useState('1');
  const [extfCurrency, setExtfCurrency] = useState('EUR');
  const [extfChartOfAccounts, setExtfChartOfAccounts] = useState('');
  const [extfIndustrySolutionId, setExtfIndustrySolutionId] = useState('');
  const [extfApplicationInfo, setExtfApplicationInfo] = useState('');

  // Set default dates on mount
  useEffect(() => {
    const today = new Date();
    
    // Fiscal year start: January 1st of current year
    const fiscalYearStart = new Date(today.getFullYear(), 0, 1);
    const fiscalYearStartStr = fiscalYearStart.toISOString().split('T')[0];
    
    // Period start/end: First and last day of last month
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const periodStart = new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 1);
    const periodEnd = new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0);
    
    const periodStartStr = periodStart.toISOString().split('T')[0];
    const periodEndStr = periodEnd.toISOString().split('T')[0];
    
    // Only set if not already set (allow user to override)
    if (!extfFiscalYearStart) {
      setExtfFiscalYearStart(fiscalYearStartStr);
    }
    if (!extfPeriodStart) {
      setExtfPeriodStart(periodStartStr);
    }
    if (!extfPeriodEnd) {
      setExtfPeriodEnd(periodEndStr);
    }
  }, []); // Only run on mount

  // Load saved client from local storage on mount
  useEffect(() => {
    try {
      const savedClientData = localStorage.getItem('selectedDatevClient');
      if (savedClientData) {
        const client = JSON.parse(savedClientData);
        setSavedClient(client);
        // Pre-fill client number with client ID
        if (client.id) {
          setExtfClientNumber(client.id);
        }
      }
    } catch (e) {
      console.warn('Failed to load saved client from local storage:', e);
    }
  }, []);

  // Load clients and handle pre-selection
  useEffect(() => {
    if (!token) return;

    (async () => {
      try {
        setLoadingClients(true);
        const res = await axios.get('/datev/clients');
        setClients(res.data.clients || []);

        // If we have a saved client, check if it exists in the current clients list and pre-select it
        if (savedClient && res.data.clients) {
          const clientExists = res.data.clients.find(c => c.id === savedClient.id);
          if (clientExists) {
            setSelectedClient(savedClient.id);
            // Ensure client number is set
            if (savedClient.id && !extfClientNumber) {
              setExtfClientNumber(savedClient.id);
            }
          }
        }
      } catch (e) {
        // ignore, may not be connected yet
      } finally {
        setLoadingClients(false);
      }
    })();
  }, [token, savedClient]);

  const onSelectClient = async (clientId) => {
    setSelectedClient(clientId);
    if (!clientId) return;
    
    // Find the client object and save to localStorage
    const client = clients.find(c => c.id === clientId);
    if (client) {
      try {
        localStorage.setItem('selectedDatevClient', JSON.stringify(client));
        setSavedClient(client);
        // Pre-fill client number with client ID
        setExtfClientNumber(client.id);
      } catch (e) {
        console.warn('Failed to save client to local storage:', e);
      }
    }
  };

  // --- Dropzones ---
  const excelAccept = { 'application/vnd.ms-excel': ['.xls'], 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] };
  const onDropExcel = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length) setExcelFile(acceptedFiles[0]);
  }, []);
  const { getRootProps: getExcelRoot, getInputProps: getExcelInput, isDragActive: isExcelDragActive } = useDropzone({
    onDrop: onDropExcel,
    multiple: false,
    accept: excelAccept,
  });

  const pdfAccept = { 'application/pdf': ['.pdf'] };
  const onDropPdfs = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length) setPdfFiles(acceptedFiles);
    else setPdfFiles([]);
  }, []);
  const { getRootProps: getPdfRoot, getInputProps: getPdfInput, isDragActive: isPdfDragActive } = useDropzone({
    onDrop: onDropPdfs,
    multiple: true,
    accept: pdfAccept,
  });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) { setError('Not authenticated'); return; }
    if (!name) { setError('Name is required'); return; }
    if (!excelFile) { setError('Excel file is required'); return; }
    if (!extfAdvisorNumber) { setError('Advisor number is required'); return; }
    if (!extfClientNumber) { setError('Client number is required'); return; }
    if (!extfFiscalYearStart) { setError('Fiscal year start is required'); return; }
    if (!extfPeriodStart) { setError('Period start date is required'); return; }
    if (!extfPeriodEnd) { setError('Period end date is required'); return; }

    const fd = new FormData();
    fd.append('name', name);
    if (description) fd.append('description', description);
    if (selectedClient) fd.append('datevClientId', selectedClient);
    
    // Ensure excel_file is appended with the actual file object
    if (!excelFile) {
      setError('Excel file is required');
      setSubmitting(false);
      return;
    }
    
    // Append file - react-dropzone provides File objects directly
    fd.append('excel_file', excelFile);
    for (const f of pdfFiles) {
      fd.append('pdfs', f);
    }
    
    // EXTF Header fields
    fd.append('extfAdvisorNumber', extfAdvisorNumber);
    fd.append('extfClientNumber', extfClientNumber);
    fd.append('extfFiscalYearStart', extfFiscalYearStart);
    fd.append('extfAccountLength', extfAccountLength);
    fd.append('extfPeriodStart', extfPeriodStart);
    fd.append('extfPeriodEnd', extfPeriodEnd);
    if (extfBatchDescription) fd.append('extfBatchDescription', extfBatchDescription);
    if (extfDictationAbbr) fd.append('extfDictationAbbr', extfDictationAbbr);
    fd.append('extfBookingType', extfBookingType);
    fd.append('extfAccountingPurpose', extfAccountingPurpose);
    fd.append('extfFinalization', extfFinalization);
    fd.append('extfCurrency', extfCurrency);
    if (extfChartOfAccounts) fd.append('extfChartOfAccounts', extfChartOfAccounts);
    if (extfIndustrySolutionId) fd.append('extfIndustrySolutionId', extfIndustrySolutionId);
    if (extfApplicationInfo) fd.append('extfApplicationInfo', extfApplicationInfo);

    // Debug: Log FormData contents
    console.log('FormData entries:');
    for (let pair of fd.entries()) {
      console.log(pair[0], pair[1]);
    }
    console.log('Excel file:', excelFile);

    setSubmitting(true);
    try {
      // Don't set Content-Type header - axios will set it automatically with boundary for FormData
      const res = await axios.post('/jobs', fd);
      if (res.status !== 200) throw new Error(res?.data?.detail || 'Failed to create job');
      navigate('/jobs');
    } catch (e) {
      // Handle validation errors (array of error objects) or simple error messages
      let errorMessage = e.message || 'An error occurred';
      if (e?.response?.data) {
        if (Array.isArray(e.response.data.detail)) {
          // FastAPI validation errors are arrays
          errorMessage = e.response.data.detail.map(err => {
            const field = err.loc ? err.loc.join('.') : 'field';
            return `${field}: ${err.msg}`;
          }).join(', ');
        } else if (typeof e.response.data.detail === 'string') {
          errorMessage = e.response.data.detail;
        } else if (e.response.data.message) {
          errorMessage = e.response.data.message;
        }
      }
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="landing">
        <div className="landing-inner">
          <h1>Create Job</h1>
          <p>Please sign in first.</p>
          <div className="actions">
            <Link className="btn-primary" to="/login">Login</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <Topbar />
      <main className="content">
        <h2>Create Job</h2>
        <div className="card">
          {error && <div className="alert error">{error}</div>}
          <form onSubmit={onSubmit} className="form" encType="multipart/form-data">
          <label className="label">Name</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. March 2025 package" />

          <label className="label">Description</label>
          <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional description" />

          <div>
            <label className="label">DATEV Client</label>
            <select className="input" value={selectedClient} onChange={(e) => onSelectClient(e.target.value)}>
              <option value="">Select client (optional)</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {savedClient && c.id === savedClient.id && ' ⭐ (Last selected)'}
                </option>
              ))}
            </select>
            {savedClient && !selectedClient && (
              <div className="text-sm text-blue-600 mt-1">
                💡 Previously selected: <strong>{savedClient.name}</strong>
              </div>
            )}
          </div>

          <label className="label">Excel file</label>
          <div style={{ marginBottom: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button type="button" className="btn-outline" onClick={() => setShowExcelFormatModal(true)} style={{ fontSize: '14px' }}>
              📋 Excel Format Instructions
            </button>
          </div>
          <div {...getExcelRoot()} className={`dropzone ${isExcelDragActive ? 'drag-active' : ''}`}>
            <input {...getExcelInput()} />
            {excelFile ? (
              <div>
                <div><strong>{excelFile.name}</strong> {typeof excelFile.size === 'number' ? `(${(excelFile.size/1024).toFixed(1)} KB)` : ''}</div>
                <button type="button" className="btn-outline" style={{ marginTop: 8 }} onClick={() => setExcelFile(null)}>Replace file</button>
              </div>
            ) : (
              <p>Drag & drop an Excel file (.xlsx or .xls) here, or click to select</p>
            )}
          </div>

          <label className="label">PDF documents (optional)</label>
          <div style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>
            Upload PDFs that match the filenames in your Excel file. These will be uploaded to DATEV and linked to the booking entries.
          </div>
          <div {...getPdfRoot()} className={`dropzone ${isPdfDragActive ? 'drag-active' : ''}`}>
            <input {...getPdfInput()} />
            {pdfFiles && pdfFiles.length > 0 ? (
              <div>
                <div><strong>{pdfFiles.length}</strong> file(s) selected</div>
                <ul style={{ textAlign: 'left', maxHeight: 150, overflow: 'auto', paddingLeft: 18, margin: '8px 0 0' }}>
                  {pdfFiles.map((f, idx) => (<li key={idx}>{f.name}</li>))}
                </ul>
                <button type="button" className="btn-outline" style={{ marginTop: 8 }} onClick={() => setPdfFiles([])}>Clear selection</button>
              </div>
            ) : (
              <p>Drag & drop PDF files here, or click to select (optional)</p>
            )}
          </div>

          <h3 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '18px', fontWeight: '600' }}>EXTF Header Information</h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="label">Advisor Number (Beraternummer) *</label>
              <input className="input" type="text" value={extfAdvisorNumber} onChange={(e) => setExtfAdvisorNumber(e.target.value)} placeholder="e.g. 29098" required />
            </div>
            <div>
              <label className="label">Client Number (Mandantennummer) *</label>
              <input className="input" type="text" value={extfClientNumber} onChange={(e) => setExtfClientNumber(e.target.value)} placeholder="e.g. 55003" required />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label className="label">Fiscal Year Start (WJ-Beginn) *</label>
              <input className="input" type="date" value={extfFiscalYearStart} onChange={(e) => setExtfFiscalYearStart(e.target.value)} required />
            </div>
            <div>
              <label className="label">Period Start (Datum von) *</label>
              <input className="input" type="date" value={extfPeriodStart} onChange={(e) => setExtfPeriodStart(e.target.value)} required />
            </div>
            <div>
              <label className="label">Period End (Datum bis) *</label>
              <input className="input" type="date" value={extfPeriodEnd} onChange={(e) => setExtfPeriodEnd(e.target.value)} required />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label className="label">Account Length (Sachkontenlänge)</label>
              <select className="input" value={extfAccountLength} onChange={(e) => setExtfAccountLength(e.target.value)}>
                <option value="4">4</option>
                <option value="5">5</option>
                <option value="6">6</option>
                <option value="7">7</option>
                <option value="8">8</option>
              </select>
            </div>
            <div>
              <label className="label">Booking Type (Buchungstyp)</label>
              <select className="input" value={extfBookingType} onChange={(e) => setExtfBookingType(e.target.value)}>
                <option value="1">1 - Financial accounting</option>
                <option value="2">2 - Annual financial statements</option>
              </select>
            </div>
            <div>
              <label className="label">Accounting Purpose (Rechnungslegungszweck)</label>
              <select className="input" value={extfAccountingPurpose} onChange={(e) => setExtfAccountingPurpose(e.target.value)}>
                <option value="0">0 - Independent</option>
                <option value="30">30 - Tax law</option>
                <option value="40">40 - Cost accounting</option>
                <option value="50">50 - Commercial law</option>
                <option value="64">64 - IFRS</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label className="label">Finalization (Festschreibung)</label>
              <select className="input" value={extfFinalization} onChange={(e) => setExtfFinalization(e.target.value)}>
                <option value="0">0 - No finalization</option>
                <option value="1">1 - Finalization</option>
              </select>
            </div>
            <div>
              <label className="label">Currency (WKZ)</label>
              <input className="input" type="text" value={extfCurrency} onChange={(e) => setExtfCurrency(e.target.value.toUpperCase())} placeholder="EUR" maxLength={3} />
            </div>
            <div>
              <label className="label">Dictation Abbreviation (Diktatkürzel)</label>
              <input className="input" type="text" value={extfDictationAbbr} onChange={(e) => setExtfDictationAbbr(e.target.value.toUpperCase())} placeholder="e.g. WD" maxLength={2} />
            </div>
          </div>

          <div>
            <label className="label">Batch Description (Bezeichnung)</label>
            <input className="input" type="text" value={extfBatchDescription} onChange={(e) => setExtfBatchDescription(e.target.value)} placeholder="e.g. Rechnungsausgang 02/2024" maxLength={30} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="label">Chart of Accounts (Sachkontenrahmen)</label>
              <input className="input" type="text" value={extfChartOfAccounts} onChange={(e) => setExtfChartOfAccounts(e.target.value)} placeholder="e.g. 03" maxLength={4} />
            </div>
            <div>
              <label className="label">Industry Solution ID</label>
              <input className="input" type="text" value={extfIndustrySolutionId} onChange={(e) => setExtfIndustrySolutionId(e.target.value)} placeholder="Optional" maxLength={4} />
            </div>
          </div>

          <div>
            <label className="label">Application Info (Anwendungsinformation)</label>
            <input className="input" type="text" value={extfApplicationInfo} onChange={(e) => setExtfApplicationInfo(e.target.value)} placeholder="e.g. 02/2024" maxLength={16} />
          </div>

          <button className="btn-primary" type="submit" disabled={submitting}>{submitting ? 'Creating...' : 'Create job'}</button>
        </form>
        </div>
      </main>

      {/* Excel Format Modal */}
      {showExcelFormatModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setShowExcelFormatModal(false)}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Excel File Format Requirements</h3>
              <button type="button" onClick={() => setShowExcelFormatModal(false)} style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666'
              }}>×</button>
            </div>
            <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
              <p style={{ marginBottom: '12px' }}>Your Excel file must include the following columns (case-insensitive):</p>
              <ul style={{ paddingLeft: '20px', marginBottom: '16px' }}>
                <li><strong>Soll/Haben</strong> - Debit/Credit indicator: must contain "S" (Debit) or "H" (Credit)</li>
                <li><strong>Description</strong> - Booking description/text (max 60 characters)</li>
                <li><strong>Amount</strong> - Transaction amount (use comma as decimal separator, e.g., 100,18)</li>
                <li><strong>CCY</strong> - Currency code (ISO 3-letter code, e.g., EUR)</li>
                <li><strong>Filename</strong> - PDF filename (without extension) that matches the PDF uploaded to DATEV</li>
                <li><strong>Konto</strong> - Account number (general ledger account)</li>
                <li><strong>Gegenkonto</strong> - Counter account number</li>
                <li><strong>BU-Schlüssel</strong> - Control key (4 digits, optional)</li>
                <li><strong>Belegdatum</strong> - Document date (format: DDMM, year from header)</li>
                <li><strong>Belegfeld 1</strong> - Invoice/document number (optional, max 36 chars)</li>
              </ul>
              <div style={{ backgroundColor: '#f8f9fa', padding: '12px', borderRadius: '4px', marginBottom: '16px' }}>
                <p style={{ margin: '0 0 8px 0', fontWeight: '600' }}>Example row:</p>
                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#e9ecef' }}>
                      <th style={{ padding: '4px', border: '1px solid #dee2e6' }}>Soll/Haben</th>
                      <th style={{ padding: '4px', border: '1px solid #dee2e6' }}>Description</th>
                      <th style={{ padding: '4px', border: '1px solid #dee2e6' }}>Amount</th>
                      <th style={{ padding: '4px', border: '1px solid #dee2e6' }}>CCY</th>
                      <th style={{ padding: '4px', border: '1px solid #dee2e6' }}>Filename</th>
                      <th style={{ padding: '4px', border: '1px solid #dee2e6' }}>Konto</th>
                      <th style={{ padding: '4px', border: '1px solid #dee2e6' }}>Gegenkonto</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '4px', border: '1px solid #dee2e6' }}>S</td>
                      <td style={{ padding: '4px', border: '1px solid #dee2e6' }}>Invoice payment</td>
                      <td style={{ padding: '4px', border: '1px solid #dee2e6' }}>100,18</td>
                      <td style={{ padding: '4px', border: '1px solid #dee2e6' }}>EUR</td>
                      <td style={{ padding: '4px', border: '1px solid #dee2e6' }}>invoice_001</td>
                      <td style={{ padding: '4px', border: '1px solid #dee2e6' }}>48400</td>
                      <td style={{ padding: '4px', border: '1px solid #dee2e6' }}>8401</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p style={{ margin: '16px 0 0 0', fontSize: '13px', color: '#666' }}>
                <strong>Note:</strong> The Filename column should match the PDF filename (without extension) that will be uploaded to DATEV. 
                The system will link the PDF GUID from DATEV to the booking entry in the generated EXTF CSV.
              </p>
            </div>
            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <button type="button" className="btn-primary" onClick={() => setShowExcelFormatModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
